import styled from '@emotion/styled';
import { useState } from 'react';
import { Artwork } from '../data/mutatingWondersData';

const Card = styled.div`
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  }
`;

const ImageContainer = styled.div`
  width: 100%;
  height: 350px;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  
  @media (min-width: 768px) {
    height: 400px;
  }

  @media (min-width: 1024px) {
    height: 450px;
  }
`;

const ArtworkImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const PlaceholderText = styled.div`
  color: #999;
  font-size: 0.9rem;
  text-align: center;
  padding: 1rem;
`;

const Content = styled.div`
  padding: 1.25rem;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const Title = styled.h3`
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  font-weight: 600;
  line-height: 1.3;
  color: #000;
`;

const Medium = styled.p`
  color: #555;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  line-height: 1.4;
`;

const PriceSection = styled.div`
  margin-top: auto;
  border-top: 1px solid #f0f0f0;
  padding-top: 1rem;
`;

const PriceLabel = styled.div`
  font-size: 0.8rem;
  color: #666;
  margin-bottom: 0.25rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 500;
`;

const Price = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: #000;
  margin-bottom: 0.5rem;
`;

const FramePriceContainer = styled.div`
  margin-top: 0.5rem;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  cursor: pointer;
  padding: 0.5rem;
  box-sizing: border-box;
  
  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const ModalContent = styled.div`
  display: flex;
  max-width: 90%;
  max-height: 90%;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  cursor: default;
  
  @media (max-width: 768px) {
    flex-direction: column;
    max-width: 95%;
    max-height: 95%;
  }
`;

const ModalImageContainer = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f8f8;
  
  @media (max-width: 768px) {
    height: 50vh;
  }
`;

const ModalImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

const ModalDetails = styled.div`
  width: 300px;
  padding: 2rem;
  background: white;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    width: 100%;
    padding: 1.5rem;
  }
`;

const ModalTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  font-weight: 600;
  color: #000;
  line-height: 1.3;
`;

const ModalMedium = styled.p`
  color: #666;
  font-size: 1rem;
  margin-bottom: 1.5rem;
  line-height: 1.4;
`;

const ModalPriceSection = styled.div`
  margin-top: auto;
  border-top: 1px solid #eee;
  padding-top: 1.5rem;
`;

const InquiryButton = styled.a`
  display: inline-block;
  background: #000;
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 600;
  text-align: center;
  margin-top: 1.5rem;
  transition: all 0.2s ease;
  
  &:hover {
    background: #333;
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(0, 0, 0, 0.7);
  border: none;
  color: white;
  font-size: 24px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;
  z-index: 1001;

  &:hover {
    background: rgba(0, 0, 0, 0.9);
  }
`;

interface MutatingWondersArtworkCardProps {
  artwork: Artwork;
}

const formatPrice = (price: string): string => {
  if (price === "N/A" || price === "Frame not available") {
    return price;
  }
  return `${price} Baht`;
};

export const MutatingWondersArtworkCard = ({ artwork }: MutatingWondersArtworkCardProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleImageClick = () => {
    if (artwork.image) {
      setIsModalOpen(true);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleModalClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleModalClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleModalClose();
    }
  };

  const generateInquiryEmail = () => {
    const subject = encodeURIComponent(`Inquiry about "${artwork.title}"`);
    const body = encodeURIComponent(
      `Hello,\n\nI am interested in learning more about the artwork:\n\nTitle: ${artwork.title}\nMedium: ${artwork.medium}\nPrice: ${formatPrice(artwork.salesPrice)}\n\nCould you please provide more information about this piece?\n\nThank you,`
    );
    return `mailto:sheena.miyake@gmail.com?subject=${subject}&body=${body}`;
  };

  return (
    <>
      <Card>
        <ImageContainer onClick={handleImageClick}>
          {artwork.image ? (
            <ArtworkImage src={artwork.image} alt={artwork.title} />
          ) : (
            <PlaceholderText>Image coming soon</PlaceholderText>
          )}
        </ImageContainer>
        <Content>
          <Title>{artwork.title}</Title>
          <Medium>{artwork.medium}</Medium>
          <PriceSection>
            <PriceLabel>Sales Price</PriceLabel>
            <Price>{formatPrice(artwork.salesPrice)}</Price>
            {artwork.salesPriceWithFrame && (
              <FramePriceContainer>
                <PriceLabel>With Frame</PriceLabel>
                <Price>{formatPrice(artwork.salesPriceWithFrame)}</Price>
              </FramePriceContainer>
            )}
          </PriceSection>
        </Content>
      </Card>
      
      {isModalOpen && artwork.image && (
        <Modal 
          onClick={handleModalClick}
          onKeyDown={handleKeyDown}
          tabIndex={0}
        >
          <CloseButton onClick={handleModalClose}>×</CloseButton>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalImageContainer>
              <ModalImage src={artwork.image} alt={artwork.title} />
            </ModalImageContainer>
            <ModalDetails>
              <ModalTitle>{artwork.title}</ModalTitle>
              <ModalMedium>{artwork.medium}</ModalMedium>
              <ModalPriceSection>
                <PriceLabel>Sales Price</PriceLabel>
                <Price>{formatPrice(artwork.salesPrice)}</Price>
                {artwork.salesPriceWithFrame && (
                  <FramePriceContainer>
                    <PriceLabel>With Frame</PriceLabel>
                    <Price>{formatPrice(artwork.salesPriceWithFrame)}</Price>
                  </FramePriceContainer>
                )}
                <InquiryButton href={generateInquiryEmail()}>
                  Purchase Inquiry
                </InquiryButton>
              </ModalPriceSection>
            </ModalDetails>
          </ModalContent>
        </Modal>
      )}
    </>
  );
}; 